import { useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";
import { authApi } from "@/lib/humhub-api/auth";

interface LoginResult {
  auth_token: string;
  expired_at: number;
}

interface LoginError {
  message: string;
  details?: unknown;
}

export const useLogin = () => {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);

  /**
   * HumHubログイン実行（OIDCトークンを明示的に設定）
   */
  const login = useCallback(
    async (customAccessToken?: string): Promise<LoginResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const oidcAccessToken = customAccessToken || auth.user?.access_token;

        if (!oidcAccessToken) {
          throw new Error("No OIDC access token available");
        }

        // OIDCアクセストークンを明示的に設定してHumHubログインAPI呼び出し
        const result = await authApi.oidcLogin(oidcAccessToken);

        if (!result?.auth_token) {
          throw new Error(result?.message || "No auth token returned");
        }

        return {
          auth_token: result.auth_token,
          expired_at: Number.parseInt(result.expired_at, 10),
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        const loginError: LoginError = {
          message: errorMessage,
          details: err,
        };

        console.error("❌ HumHub login failed:", loginError);
        setError(loginError);
        throw loginError;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.user],
  );

  return {
    login,
    isLoading,
    error,
  };
};
